import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API, authHeaders, useAdminAuthError } from "@/lib/adminApi";
import { PROVIDER_LABELS } from "@/lib/adminUtils";
import PageHeader from "@/components/admin/PageHeader";
import StatusBadge from "@/components/admin/StatusBadge";
import Spinner from "@/components/admin/Spinner";

const STATUS_LABELS = {
  pending: { label: "En attente", color: "amber" },
  paid:    { label: "Payé", color: "emerald" },
};

const eur = (v) => (v == null ? "—" : `${Number(v).toLocaleString("fr-FR")} €`);
const fmtDate = (v) => {
  if (!v) return "—";
  // date_of_death peut être JJ/MM/AAAA ou ISO (même convention que birth_date)
  if (v.includes("/")) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString("fr-FR");
};

/* ── Sinistres — vue d'ensemble des décès déclarés et de leur financement.
 *    Les actions (déclarer / payer / annuler) se font depuis la fiche membre ;
 *    cette page est la vue d'ensemble, avec un lien direct vers chaque fiche. */
export const SinistresPage = () => {
  const handleAuthError = useAdminAuthError();

  const [claims, setClaims] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [claimsRes, membersRes] = await Promise.all([
        axios.get(`${API}/claims/admin-list`, { headers: authHeaders() }),
        axios.get(`${API}/members/`, { headers: authHeaders() }),
      ]);
      setClaims(claimsRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erreur lors du chargement des sinistres.");
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const memberById = members.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});
  const parentName = (c) => {
    const m = memberById[c.member_id];
    return m ? `${m.first_name || ""} ${m.last_name || ""}`.trim() || "—" : "—";
  };
  const displayName = (c) =>
    c.beneficiary_type === "child"
      ? `${c.child_first_name || ""} ${c.child_last_name || ""}`.trim() || "—"
      : parentName(c);

  const filteredClaims = claims.filter((c) => {
    const matchSearch =
      displayName(c).toLowerCase().includes(search.toLowerCase()) ||
      parentName(c).toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalPaid = filteredClaims
    .filter((c) => c.status === "paid")
    .reduce((sum, c) => sum + (Number(c.paid_amount) || 0), 0);

  return (
    <section className="fade-up fade-up-1">
      <PageHeader
        title="Sinistres"
        subtitle={`${filteredClaims.length} sinistre${filteredClaims.length !== 1 ? "s" : ""} · ${totalPaid.toLocaleString("fr-FR")} € versés`}
        actions={
          <>
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher un membre…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 w-48 transition-all"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="all">Tous statuts</option>
              <option value="pending">En attente</option>
              <option value="paid">Payé</option>
            </select>
          </>
        }
      />

      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32"><Spinner /></div>
        ) : filteredClaims.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm gap-2">
            <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>Aucun sinistre déclaré.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <p className="text-[10px] text-slate-400 text-right px-4 pt-2 pb-0 sm:hidden">← Faites défiler →</p>
            <table className="text-sm" style={{ minWidth: "720px", width: "100%" }}>
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  {["Nom", "Type", "Date de décès", "Déclaré le", "Montant", "Mode", "Statut", ""].map((label, i) => (
                    <th
                      key={i}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredClaims.map((c, i) => {
                  const s = STATUS_LABELS[c.status] || { label: c.status, color: "slate" };
                  const amount = c.status === "paid" ? c.paid_amount : c.estimated_amount;
                  return (
                    <tr
                      key={c.id}
                      className={`hover:bg-slate-50/40 transition-colors ${i < filteredClaims.length - 1 ? "border-b border-slate-50" : ""}`}
                    >
                      <td className="px-4 py-3.5 font-semibold text-slate-900 whitespace-nowrap">
                        {displayName(c)}
                        {c.beneficiary_type === "child" && (
                          <span className="block text-xs font-normal text-slate-400">enfant de {parentName(c)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">
                        {c.beneficiary_type === "child" ? "Enfant" : "Membre"}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">{fmtDate(c.date_of_death)}</td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">
                        {c.declared_at ? new Date(c.declared_at).toLocaleDateString("fr-FR") : "—"}
                      </td>
                      <td className="px-4 py-3.5 text-slate-700 font-medium whitespace-nowrap">
                        {eur(amount)}{c.status === "pending" && amount != null ? " (estimé)" : ""}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap capitalize">
                        {c.payment_method ? (PROVIDER_LABELS[c.payment_method] || c.payment_method) : "—"}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <StatusBadge color={s.color}>{s.label}</StatusBadge>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-right">
                        <Link
                          to={`/app/membres?member=${c.member_id}`}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all hover:bg-slate-50"
                          style={{ borderColor: "#059669", color: "#059669" }}
                        >
                          Voir la fiche →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default SinistresPage;
